export function ComplianceRequirements() {
  const requirements = [
    {
      category: 'Occupancy Limits',
      rules: [
        'Maximum 2 guests per bedroom',
        'Total occupancy cannot exceed fire code limits',
        'Must maintain accurate guest records',
        'No events or parties permitted'
      ],
      icon: 'ri-group-line',
      color: 'bg-blue-500'
    },
    {
      category: 'Safety Standards',
      rules: [
        'Working smoke detectors on every floor',
        'Carbon monoxide detectors required',
        'Fire extinguisher accessible',
        'Emergency exit plan posted'
      ],
      icon: 'ri-shield-check-line',
      color: 'bg-green-500'
    },
    {
      category: 'Parking & Access',
      rules: [
        'Maintain required parking spaces',
        'Cannot rent parking separately',
        'Clear access for emergency vehicles',
        'Snow removal in winter months'
      ],
      icon: 'ri-parking-box-line',
      color: 'bg-purple-500'
    },
    {
      category: 'Neighbor Relations',
      rules: [
        'Provide contact info to neighbors',
        'Respond to complaints within 24 hours',
        'Maintain quiet hours (10pm-7am)',
        'Keep property well-maintained'
      ],
      icon: 'ri-community-line',
      color: 'bg-orange-500'
    },
    {
      category: 'Tax Obligations',
      rules: [
        'Collect and remit 8% Provincial Sales Tax',
        'Collect 3% Municipal and Regional Tax',
        'Report income on tax returns',
        'Keep records for 7 years'
      ],
      icon: 'ri-calculator-line',
      color: 'bg-red-500'
    },
    {
      category: 'Platform Compliance',
      rules: [
        'Display business license number in listing',
        'Accurate property description',
        'Current photos and amenities',
        'Respond to platform inquiries promptly'
      ],
      icon: 'ri-global-line',
      color: 'bg-teal-500'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Ongoing Compliance Requirements
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Getting licensed is just the beginning. Here are the ongoing requirements you must maintain to stay compliant in North Vancouver.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {requirements.map((req, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#882629] rounded-xl mb-6">
                <i className={`${req.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-[#505A5A] mb-4">
                {req.category}
              </h3>
              <ul className="space-y-3">
                {req.rules.map((rule, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#882629] text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-sm text-[#505A5A]/80 leading-relaxed">
                      {rule}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-10 shadow-xl mb-12">
          <div className="flex items-start gap-6 mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-[#882629] rounded-xl flex-shrink-0">
              <i className="ri-alert-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[#505A5A] mb-3">
                Enforcement & Penalties
              </h3>
              <p className="text-[#505A5A]/70 leading-relaxed">
                Both District and City of North Vancouver actively monitor short term rental compliance through online platforms and complaint investigations. Violations can result in significant penalties.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#F0F0F0] rounded-xl p-6">
              <div className="text-3xl font-bold text-[#882629] mb-2">
                $1,000
              </div>
              <div className="text-sm font-semibold text-[#505A5A] mb-2">
                First Offense
              </div>
              <p className="text-sm text-[#505A5A]/70">
                Operating without license or minor violations
              </p>
            </div>

            <div className="bg-[#F0F0F0] rounded-xl p-6">
              <div className="text-3xl font-bold text-[#882629] mb-2">
                $5,000
              </div>
              <div className="text-sm font-semibold text-[#505A5A] mb-2">
                Repeat Violations
              </div>
              <p className="text-sm text-[#505A5A]/70">
                Continued non-compliance after warning
              </p>
            </div>

            <div className="bg-[#F0F0F0] rounded-xl p-6">
              <div className="text-3xl font-bold text-[#882629] mb-2">
                $10,000+
              </div>
              <div className="text-sm font-semibold text-[#505A5A] mb-2">
                Serious Violations
              </div>
              <p className="text-sm text-[#505A5A]/70">
                Safety violations or fraudulent applications
              </p>
            </div>
          </div>
        </div>

        {/* Enforcement Style */}
        <div className="bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-shield-check-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Provincial Registry + Complaint-Based
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                North Vancouver follows BC's Provincial Registry requirements with automatic platform de-listing for non-compliant properties. Both District and City actively investigate complaints and conduct platform monitoring. Fines range from $1,000 for first offenses to $10,000+ for serious violations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
